import { isNil } from 'es-toolkit';

import type { ClientPortalAccountPerformancePeriod } from '../../api/account-performance-query';
import type { ClientPortalTimeRange } from '../common/constants';
import type { NetWorthPoint } from './net-worth';
import { CLIENT_PORTAL_TIME_RANGES, isClientPortalTimeRange } from '../common/constants';
import { availableTimeRanges } from '../common/time-ranges';
import { toFiniteNumber } from '../common/utils';

export type AccountPeriodReturn = {
  readonly percent: number;
  readonly amount: number | null;
  readonly endDate: string | null;
};

export type AccountPeriodReturns = Partial<Record<ClientPortalTimeRange, AccountPeriodReturn>>;

const EVERY_TIME_RANGE: readonly ClientPortalTimeRange[] = CLIENT_PORTAL_TIME_RANGES.map((range) => range.value);

export function accountPeriodReturns(
  periods: readonly ClientPortalAccountPerformancePeriod[] | undefined,
): AccountPeriodReturns {
  const returns: Record<string, AccountPeriodReturn> = {};

  for (const period of periods ?? []) {
    const timePeriod = period.timePeriod;
    if (isNil(timePeriod) || !isClientPortalTimeRange(timePeriod)) {
      continue;
    }
    const twr = toFiniteNumber(period.twr);
    if (isNil(twr)) {
      continue;
    }
    returns[timePeriod] = {
      percent: twr * 100,
      amount: toFiniteNumber(period.usdReturn),
      endDate: period.endDate,
    };
  }

  return returns;
}

export function timeRangesWithReturns(
  seriesRanges: readonly ClientPortalTimeRange[],
  returns: AccountPeriodReturns,
): readonly ClientPortalTimeRange[] {
  return EVERY_TIME_RANGE.filter((range) => seriesRanges.includes(range) || !isNil(returns[range]));
}

export function accountReturnPeriods(returns: AccountPeriodReturns): readonly ClientPortalTimeRange[] {
  return EVERY_TIME_RANGE.filter((range) => !isNil(returns[range]));
}

export function accountDetailTimeRanges(
  series: readonly NetWorthPoint[],
  returns: AccountPeriodReturns,
): readonly ClientPortalTimeRange[] {
  return timeRangesWithReturns(availableTimeRanges(series.at(0)?.date, series.at(-1)?.date), returns);
}
